import { Loader2, Sparkles } from 'lucide-react';
import { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAIAssistant } from '@/contexts/ai-assistant-context';
import { AIAssistantMessage } from './ai-assistant-message';

const FALLBACK_THINKING = 'AI is thinking...';

export function AIAssistantChat() {
  const { messages, isProcessing, streamingText, thinkingMessage } = useAIAssistant();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingText]);

  const displayThinking = thinkingMessage ?? FALLBACK_THINKING;

  if (messages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <Loader2 className="h-8 w-8 text-primary animate-pulse" />
        </div>
        <h3 className="text-lg font-semibold mb-2">Start a conversation</h3>
        <p className="text-sm text-muted-foreground max-w-[280px]">
          Ask me anything about your credit control, or try one of the suggestions above.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {messages.map((message) => (
        <AIAssistantMessage key={message.id} message={message} />
      ))}

      {/* Streaming text */}
      {isProcessing && streamingText && (
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-primary" />
          </div>
          <div className="flex-1 space-y-2">
            <div className="rounded-lg px-4 py-2 max-w-[85%] bg-muted">
              <div className="text-sm ai-message-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamingText}</ReactMarkdown>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Thinking indicator */}
      {isProcessing && !streamingText && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50 border border-border/50">
          <div className="relative">
            <Sparkles className="h-5 w-5 text-primary animate-pulse" />
            <Loader2 className="h-3 w-3 text-primary animate-spin absolute -bottom-1 -right-1" />
          </div>
          <span className="text-sm text-muted-foreground font-medium">{displayThinking}</span>
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  );
}
